import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type UploadStatus =
  | 'pending'
  | 'uploading'
  | 'completing'
  | 'completed'
  | 'failed'
  | 'aborting'
  | 'aborted';

export interface UploadProgress {
  fileId: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: UploadStatus;
  isMultipart: boolean;
  multipartId?: string;
  uploadedBytes: number;
  error?: string;
}

interface UploadProgressState {
  uploads: Record<string, UploadProgress>;
}

const initialState: UploadProgressState = {
  uploads: {},
};

const uploadProgressSlice = createSlice({
  name: 'uploadProgress',
  initialState,
  reducers: {
    addUpload: (state, action: PayloadAction<Omit<UploadProgress, 'progress' | 'status' | 'uploadedBytes'>>) => {
      const entry = action.payload;
      state.uploads[entry.fileId] = {
        ...entry,
        progress: 0,
        uploadedBytes: 0,
        status: 'pending',
      };
    },
    updateProgress: (
      state,
      action: PayloadAction<{ fileId: string; uploadedBytes: number }>,
    ) => {
      const upload = state.uploads[action.payload.fileId];
      if (upload) {
        upload.uploadedBytes = action.payload.uploadedBytes;
        upload.progress = upload.fileSize > 0
          ? Math.min(Math.round((action.payload.uploadedBytes / upload.fileSize) * 100), 100)
          : 0;
      }
    },
    setUploadStatus: (
      state,
      action: PayloadAction<{ fileId: string; status: UploadStatus; error?: string }>,
    ) => {
      const upload = state.uploads[action.payload.fileId];
      if (upload) {
        upload.status = action.payload.status;
        if (action.payload.error !== undefined) {
          upload.error = action.payload.error;
        }
      }
    },
    removeUpload: (state, action: PayloadAction<string>) => {
      delete state.uploads[action.payload];
    },
    clearAllUploads: (state) => {
      state.uploads = {};
    },
  },
});

export const {
  addUpload,
  updateProgress,
  setUploadStatus,
  removeUpload,
  clearAllUploads,
} = uploadProgressSlice.actions;

export default uploadProgressSlice.reducer;
